/**
 * What Board 3 adds to the four catalog lists — **columns and marks, and only
 * those** (`catalog-units-and-buying.md` §7). The toolbar, the chips, the
 * pager and the phone cards are the engine's; these are the cells.
 */
import type { ReactNode } from 'react'
import { Tag } from './primitives.js'
import { EM_DASH, formatNumber, formatUnitCost } from '../lib/format.js'
import type { ListRow } from '../lib/use-list.js'

/**
 * The server says the conversion as one sentence — `1 skid = 520 sf`, or
 * `Per ton` — and the cell bolds the two words that matter, muted around
 * them (Board 3: `1 <b>skid</b> = <b>520</b> sq ft`). Reading the sentence
 * rather than three columns keeps one source for the words.
 */
export function conversionParts(text: string): { buy: string; per: string | null; alloc: string } | null {
  const pair = /^1 (.+?) = ([\d.,]+) (.+)$/.exec(text)
  if (pair) return { buy: pair[1]!, per: pair[2]!, alloc: pair[3]! }
  const same = /^Per (.+)$/.exec(text)
  if (same) return { buy: same[1]!, per: null, alloc: same[1]! }
  return null
}

export function ConversionCell({ text }: { text: unknown }) {
  if (typeof text !== 'string' || !text) return <span className="text-text-faint">{EM_DASH}</span>
  const parts = conversionParts(text)
  if (!parts) return <span className="text-text-muted">{text}</span>
  return parts.per === null
    ? <span className="text-text-muted">Per <b className="font-semibold text-text">{parts.buy}</b></span>
    : (
      <span className="tnum text-text-muted">
        1 <b className="font-semibold text-text">{parts.buy}</b> ={' '}
        <b className="font-semibold text-text">{formatNumber(Number(parts.per.replace(/,/g, '')))}</b>{' '}
        {parts.alloc}
      </span>
    )
}

/** `Skid → sf` — the phone card's short form of the same sentence. */
export function conversionShort(text: unknown): string | null {
  if (typeof text !== 'string') return null
  const parts = conversionParts(text)
  if (!parts) return null
  const buy = parts.buy.charAt(0).toUpperCase() + parts.buy.slice(1)
  return `${buy} → ${parts.alloc}`
}

/**
 * The margin pill — the one the quote uses: green at 40% and over, amber from
 * 20%, red below; whole percent, tabular.
 */
export function MarginPill({ pct }: { pct: unknown }) {
  if (pct === null || pct === undefined || pct === '') {
    return <span className="text-text-faint">{EM_DASH}</span>
  }
  const margin = Number(pct)
  return (
    <Tag tone={margin >= 40 ? 'ok' : margin >= 20 ? 'warn' : 'bad'}>
      <span className="tnum">{formatNumber(Math.round(margin))}%</span>
    </Tag>
  )
}

/**
 * A unit price keeps its cents (§3.8 is about totals, not rates), and a unit
 * cost the four places Board 3 prints — `$0.9356` a square foot.
 */
export const rateCell = (value: unknown): ReactNode =>
  value === null || value === undefined || value === ''
    ? <span className="text-text-faint">{EM_DASH}</span>
    : <span className="tnum">{formatUnitCost(Number(value))}</span>

/** The bold name and the tag beside it — `Stocked`, `Owned`, `Hired in`. */
export function NameWithTag({ name, tag, tone }: {
  name: string
  tag?: string | null
  tone?: 'plain' | 'ok' | 'info'
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span className="truncate">{name}</span>
      {tag && <Tag tone={tone ?? 'plain'}>{tag}</Tag>}
    </span>
  )
}

/** "Hardscape · Ewing Supply" — whichever of the parts there are. */
export const joined = (...parts: unknown[]): string | undefined =>
  parts.filter((part) => typeof part === 'string' && part.trim()).join(' · ') || undefined

export type { ListRow }
